// Get Started — completion logic (pure, schema-driven by the tenant's app-data). See
// docs/get-started.md. Computes which setup fields are done so the page shows only what's left
// and stops prompting once the MUST tier is complete (no manual dismiss — completion is the signal).
//
// Trade-aware: the office app serves multiple verticals (PAINTER books estimates, SAAS books
// demos), which need DIFFERENT setup fields — a painter has service areas + specialties; a SaaS
// SDR has a product one-liner + ideal customer + FAQ. `compute_setup` branches on
// `trade.primary_category` and builds the right checklist. The field paths it checks line up with
// the discriminated `profile.experience` union in @captomi/captomi-office-schema.

export type Tier = 'must' | 'should';

export interface SetupItem {
  key: string;
  label: string;
  /** One-line "why this matters" shown next to the item. */
  why: string;
  tier: Tier;
  complete: boolean;
  /** The exact label this field renders with in the Profile form — used to deep-link + scroll to it
   *  (Profile reads `?focus=<field_label>`). Must match the schema's `.meta({ label })`. */
  field_label: string;
}

export interface SetupStatus {
  items: SetupItem[];
  must_total: number;
  must_done: number;
  all_must_complete: boolean;
  /** True once the one-time first-run wizard has been shown (app-data marker). */
  wizard_seen: boolean;
}

// The pilot default for the booking-notification email — bookings sent here go to Captomi, not
// the tenant. Treated as "not set" so Get Started flags it (the #1 trap). Keep in sync with
// captomi-office's resolveNotifyEmail PILOT_NOTIFY_INBOX.
export const PILOT_NOTIFY_INBOX = 'change-your-booking-email@trypainteroffice.com';

type Obj = Record<string, unknown>;

function at(data: Obj, path: string): unknown {
  return path.split('.').reduce<unknown>((o, k) => (o && typeof o === 'object' ? (o as Obj)[k] : undefined), data);
}
function non_empty_str(v: unknown): boolean {
  return typeof v === 'string' && v.trim().length > 0;
}
function non_empty_arr(v: unknown): boolean {
  return Array.isArray(v) && v.length > 0;
}

/** A booking email is "set" only when it's a real, valid address that isn't the pilot test inbox.
 *  Shared by the painter (painter_email) and SaaS (sdr_email) booking-email checks. */
export function booking_email_ok(v: unknown): boolean {
  if (!non_empty_str(v)) return false;
  const e = (v as string).trim().toLowerCase();
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e) && e !== PILOT_NOTIFY_INBOX;
}
/** @deprecated Back-compat alias for `booking_email_ok`. */
export const painter_email_ok = booking_email_ok;

type Getter = (path: string) => unknown;

/** PAINTER checklist — estimates, service areas, specialties (homeowner-facing). */
function painter_items(g: Getter): SetupItem[] {
  return [
    {
      key: 'painter_email',
      field_label: 'Painter email',
      tier: 'must',
      label: 'Booking email',
      why: 'Where your booking emails are sent. Until you set this, they go to a Captomi test inbox — not to you.',
      complete: booking_email_ok(g('profile.experience.painter_email')),
    },
    {
      key: 'service_areas',
      field_label: 'Service areas',
      tier: 'must',
      label: 'Service areas',
      why: 'So your receptionist knows where you work and can handle out-of-area callers.',
      complete: non_empty_arr(g('profile.service_areas')),
    },
    {
      key: 'specialties',
      field_label: 'Specialties',
      tier: 'must',
      label: 'Specialties',
      why: 'So your receptionist knows which jobs you take.',
      complete: non_empty_arr(g('profile.specialties')),
    },
    {
      key: 'availability',
      field_label: 'Weekly hours',
      tier: 'must',
      label: 'Availability',
      why: 'So your receptionist can offer and book estimate times on the call.',
      complete: non_empty_arr(g('availability.green_zones')),
    },
    {
      key: 'strengths',
      field_label: 'Strengths',
      tier: 'should',
      label: 'Strengths',
      why: 'Your receptionist uses this to sell your business on the call.',
      complete: non_empty_str(g('profile.strengths')),
    },
    {
      key: 'jobs_completed',
      field_label: 'Jobs completed',
      tier: 'should',
      label: 'Jobs completed',
      why: 'Adds credibility — "we’ve done 500+ jobs".',
      complete: non_empty_str(g('profile.experience.jobs_completed')),
    },
    {
      key: 'business_summary',
      field_label: 'Business summary',
      tier: 'should',
      label: 'Business summary',
      why: 'A short description of what you do, in your words.',
      complete: non_empty_str(g('profile.business_summary')),
    },
    {
      key: 'guarantee',
      field_label: 'Guarantee',
      tier: 'should',
      label: 'Guarantee',
      why: 'Answers a common homeowner question on the call.',
      complete: non_empty_str(g('profile.experience.guarantee')),
    },
  ];
}

/** SAAS checklist — demos, product knowledge, qualification (prospect-facing). */
function saas_items(g: Getter): SetupItem[] {
  return [
    {
      key: 'sdr_email',
      field_label: 'SDR email',
      tier: 'must',
      label: 'Booking email',
      why: 'Where demo invites and new-lead emails are sent. Until you set this, they go to a Captomi test inbox — not to you.',
      complete: booking_email_ok(g('profile.experience.sdr_email')),
    },
    {
      key: 'product_oneliner',
      field_label: 'Product one-liner',
      tier: 'must',
      label: 'What you sell',
      why: 'So your receptionist can tell prospects what your product does.',
      complete: non_empty_str(g('profile.experience.product_oneliner')),
    },
    {
      key: 'ideal_customer',
      field_label: 'Ideal customer',
      tier: 'must',
      label: 'Ideal customer',
      why: 'So your receptionist can tell whether a caller is a good fit before booking a demo.',
      complete: non_empty_str(g('profile.experience.ideal_customer')),
    },
    {
      key: 'availability',
      field_label: 'Weekly hours',
      tier: 'must',
      label: 'Availability',
      why: 'So your receptionist can offer and book demo times on the call.',
      complete: non_empty_arr(g('availability.green_zones')),
    },
    {
      key: 'faq',
      field_label: 'FAQ',
      tier: 'should',
      label: 'FAQ',
      why: 'So your receptionist answers common product questions instead of guessing.',
      complete: non_empty_str(g('profile.experience.faq')),
    },
    {
      key: 'pricing_summary',
      field_label: 'Pricing summary',
      tier: 'should',
      label: 'Pricing',
      why: 'So your receptionist can handle pricing questions on the call.',
      complete: non_empty_str(g('profile.experience.pricing_summary')),
    },
    {
      key: 'business_summary',
      field_label: 'Business summary',
      tier: 'should',
      label: 'Business summary',
      why: 'A short description of what your company does, in your words.',
      complete: non_empty_str(g('profile.business_summary')),
    },
    {
      key: 'competitors',
      field_label: 'Competitors',
      tier: 'should',
      label: 'Competitors',
      why: 'So your receptionist handles "how do you compare to X?" without bashing them.',
      complete: non_empty_str(g('profile.experience.competitors')),
    },
  ];
}

export function compute_setup(app_data: Obj): SetupStatus {
  const g = (p: string): unknown => at(app_data, p);

  // Pick the vertical's checklist. Default to PAINTER when the trade is absent/unknown (legacy
  // rows, or before the trade is set) so existing painter tenants are unaffected.
  const items = g('trade.primary_category') === 'SAAS' ? saas_items(g) : painter_items(g);

  const must = items.filter((i) => i.tier === 'must');
  const must_done = must.filter((i) => i.complete).length;
  return {
    items,
    must_total: must.length,
    must_done,
    all_must_complete: must_done === must.length,
    wizard_seen: Boolean(g('profile.get_started_wizard_seen_at')),
  };
}
